import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";
import supabase from "../../../utils/supabase";
import useUserStore from "../../../stores/user";
import { toast } from "react-toastify";
import { StudentClassAutocomplete } from "../../StudentClassAutocomplete";
import { getStandardTerminology } from "../../../utils/standardsUtils";
import { useT } from "../../../stores/uiLang";
import { TD_T } from "../../../i18n/teacherDashboard";

interface StudentSubmission {
  id: string;
  student_id: string;
  assignment_id: string;
  status: string;
  submitted_at: string;
  grade?: string;
  grade_percentage?: number;
  student: {
    fullname: string;
  };
  assignment: {
    assessment_name: string;
    type: string;
    standard: string;
    due_date: string;
  };
}

interface AssignmentSubmissionsProps {
  activeFilter?: string;
}

interface Class {
  id: string;
  name: string;
}

export default function AssignmentSubmissions({
  activeFilter,
}: AssignmentSubmissionsProps) {
  const t = useT(TD_T);
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<StudentSubmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("completed");
  const [classFilter, setClassFilter] = useState<string>("all");
  const [studentFilter, setStudentFilter] = useState<string>("");
  const [standardFilter, setStandardFilter] = useState<string>("all");
  const [availableClasses, setAvailableClasses] = useState<Class[]>([]);
  const [availableStandards, setAvailableStandards] = useState<string[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const stateName = useUserStore((state) => state.stateName);
  const terminology = getStandardTerminology(stateName || "Oklahoma");
  const { user } = useUserStore();
  const itemsPerPage = 5;

  const fetchAvailableClasses = async () => {
    if (!user?.id) return;

    setLoadingClasses(true);
    try {
      // Get classes that the teacher has access to
      const { data: classes, error } = await supabase
        .from("classes")
        .select("id, name")
        .eq("user_id", user.id)
        .order("name");

      if (error) {
        console.error("Error fetching classes:", error);
        toast.error("Failed to fetch classes");
        return;
      }

      setAvailableClasses(classes || []);
    } catch (error) {
      console.error("Error fetching classes:", error);
      toast.error("Failed to fetch classes");
    } finally {
      setLoadingClasses(false);
    }
  };

  const fetchAvailableStandards = async () => {
    if (!user?.id) return;

    try {
      const { data: standards, error } = await supabase
        .from("assignments")
        .select("standard")
        .eq("user_id", user.id)
        .not("standard", "is", null);

      if (error) {
        console.error("Error fetching standards:", error);
        toast.error("Failed to fetch standards");
        return;
      }

      const allStandardNumbers = new Set<number>();

      standards?.forEach((s) => {
        if (s.standard) {
          const standard = s.standard.trim();

          if (standard.includes("-")) {
            const rangeMatch = standard.match(/(\d+)-(\d+)/);
            if (rangeMatch) {
              const start = parseInt(rangeMatch[1]);
              const end = parseInt(rangeMatch[2]);
              for (let i = start; i <= end; i++) {
                allStandardNumbers.add(i);
              }
            }
          } else if (standard.includes(",")) {
            const numbers = standard.match(/\d+/g);
            numbers?.forEach((num) => allStandardNumbers.add(parseInt(num)));
          } else {
            const numberMatch = standard.match(/\d+/);
            if (numberMatch) {
              allStandardNumbers.add(parseInt(numberMatch[0]));
            }
          }
        }
      });

      console.log(terminology);

      const individualStandards = Array.from(allStandardNumbers)
        .sort((a, b) => a - b)
        .map((num) => `${terminology} ${num}`);

      setAvailableStandards(individualStandards);

      console.log(individualStandards);
    } catch (error) {
      console.error("Error fetching standards:", error);
      toast.error("Failed to fetch standards");
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchSubmissions(1);
  }, [
    user?.id,
    activeFilter,
    statusFilter,
    classFilter,
    studentFilter,
    standardFilter,
  ]);

  useEffect(() => {
    if (user?.id) {
      fetchAvailableClasses();
      fetchAvailableStandards();
    }
  }, [user?.id, stateName]);

  useEffect(() => {
    setStudentFilter("");
  }, [classFilter]);

  useEffect(() => {
    fetchSubmissions(currentPage);
  }, [currentPage]);

  const fetchSubmissions = async (page: number = 1) => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const from = (page - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;

      let query = supabase
        .from("student_assignments")
        .select(
          `
          id,
          student_id,
          assignment_id,
          status,
          submitted_at,
          grade,
          grade_percentage,
          assignments!inner(
            id,
            assessment_name,
            type,
            standard,
            due_date,
            user_id
          ),
          profiles!student_id(
            id,
            firstname,
            lastname
          )
        `,
          { count: "exact" }
        )
        .eq("assignments.user_id", user.id);

      if (activeFilter && activeFilter !== "All") {
        const assignmentType = activeFilter.toLowerCase().slice(0, -1);
        query = query.eq("assignments.type", assignmentType);
      }

      if (statusFilter) {
        query = query.eq("status", statusFilter);
      }

      if (classFilter && classFilter !== "all") {
        const { data: classStudents } = await supabase
          .from("class_students")
          .select("user_id")
          .eq("class_id", classFilter);

        if (classStudents && classStudents.length > 0) {
          const studentIds = classStudents.map((cs) => cs.user_id);
          query = query.in("student_id", studentIds);
        } else {
          query = query.in("student_id", []);
        }
      }

      if (studentFilter) {
        query = query.eq("student_id", studentFilter);
      }

      if (standardFilter && standardFilter !== "all") {
        const standardNumber = standardFilter
          .replace(`${terminology} `, "")
          .trim();
        query = query.ilike("assignments.standard", `%${standardNumber}%`);
      }

      const {
        data: studentAssignments,
        error: studentAssignmentsError,
        count,
      } = await query.order("created_at", { ascending: false }).range(from, to);

      if (studentAssignmentsError) {
        console.error(
          "Error fetching student assignments:",
          studentAssignmentsError
        );
        toast.error("Failed to fetch student assignments");
        return;
      }

      if (!studentAssignments || studentAssignments.length === 0) {
        setSubmissions([]);
        setTotalCount(0);
        return;
      }

      setTotalCount(count || 0);

      const transformedData = studentAssignments.map((sa: any) => {
        const profile = sa.profiles;
        const assignment = sa.assignments;

        const transformedSubmission = {
          id: sa.id,
          student_id: sa.student_id,
          assignment_id: sa.assignment_id,
          status: sa.status,
          submitted_at: sa.submitted_at,
          grade: sa.grade,
          grade_percentage: sa.grade_percentage,
          student: {
            fullname: profile?.firstname + " " + profile?.lastname || "",
          },
          assignment: {
            assessment_name: assignment?.assessment_name || "No Name",
            type: assignment?.type || "No Type",
            standard: assignment?.standard || "No Standard",
            due_date: assignment?.due_date || "No Date",
          },
        };

        return transformedSubmission;
      });

      setSubmissions(transformedData);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      toast.error("Failed to fetch submissions");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "in_progress":
        return "bg-yellow-100 text-yellow-800";
      case "not_started":
        return "bg-gray-100 text-gray-800";
      case "overdue":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "quiz":
        return "bg-blue-100 text-blue-800";
      case "midterm":
        return "bg-purple-100 text-purple-800";
      case "final":
        return "bg-green-100 text-green-800";
      case "project":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handlePrevious = () => {
    if (currentPage > 1 && !loading) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    if (currentPage < totalPages && !loading) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-md">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              {t.submissionsTitle}
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              {t.submissionsSub}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loadingClasses}
            >
              <option value="all">{t.allClasses}</option>
              {availableClasses.map((cls) => (
                <option key={cls.id} value={cls.id}>
                  {cls.name}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="completed">{t.optCompleted}</option>
              <option value="in_progress">{t.legendInProgress}</option>
              <option value="not_started">{t.legendNotStarted}</option>
              <option value="overdue">{t.optOverdue}</option>
            </select>
            <select
              value={standardFilter}
              onChange={(e) => setStandardFilter(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">
                {t.allRecords}
              </option>
              {availableStandards.map((standard) => (
                <option key={standard} value={standard}>
                  {standard}
                </option>
              ))}
            </select>
            <StudentClassAutocomplete
              value={studentFilter}
              onChange={setStudentFilter}
              selectedClassId={classFilter}
              placeholder={t.filterByStudent}
              className="w-48 "
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-6 text-center text-gray-500">
            {t.loadingSubmissions}
          </div>
        ) : submissions.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            {t.noSubmissions}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t.student}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t.assignment}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t.typeCol}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t.submitted}
                </th>
                {statusFilter === "completed" ? (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t.gradeCol}
                  </th>
                ) : null}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t.actions}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {submissions.map((submission) => (
                <tr key={submission.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex flex-row gap-2 justify-start items-center">
                        <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-indigo-800">
                            {submission.student.fullname
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .toUpperCase()}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-black">
                          {submission.student.fullname}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {submission.assignment.assessment_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {submission.assignment.standard}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTypeColor(
                        submission.assignment.type
                      )}`}
                    >
                      {submission.assignment.type.charAt(0).toUpperCase() +
                        submission.assignment.type.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {submission.submitted_at
                      ? formatDate(submission.submitted_at)
                      : "-"}
                  </td>
                  {statusFilter === "completed" ? (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {submission.grade_percentage !== undefined ? (
                        <div className="relative group">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full cursor-help ${
                              submission.grade_percentage >= 70
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {submission.grade ||
                              `${submission.grade_percentage}%`}
                          </span>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                            {submission.grade_percentage}%
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                  ) : null}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => {
                        navigate(`/submission-review/${submission.id}`);
                      }}
                      className="text-indigo-600 hover:text-indigo-900 flex items-center justify-end"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      {t.view}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {submissions.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {(currentPage - 1) * itemsPerPage + 1}-
              {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount}{" "}
              submissions
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrevious}
                disabled={currentPage === 1 || loading}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t.previous}
              </button>
              <button
                onClick={handleNext}
                disabled={
                  currentPage >= Math.ceil(totalCount / itemsPerPage) || loading
                }
                className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t.next}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
